import React, { useState } from 'react';
import { hapticSelection } from '../utils/haptics';

// RC Status Icon (Registration Smart Card)
const RcIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <line x1="2" y1="10" x2="22" y2="10" />
    <line x1="6" y1="14" x2="10" y2="14" />
    <line x1="14" y1="14" x2="18" y2="14" />
    <circle cx="16" cy="7.5" r="1" fill="currentColor" />
  </svg>
);

// Vehicle Insurance Icon (Vehicle + Shield)
const InsuranceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 11h11l3 4v3h-2a2 2 0 1 1-4 0H8a2 2 0 1 1-4 0H3v-4l1-3z" />
    <path d="M12 3a7 7 0 0 0-7 7c0 4 5 7 7 8 2-1 7-4 7-8a7 7 0 0 0-7-7z" fill="#eff6ff" />
    <path d="M12 3a7 7 0 0 0-7 7c0 4 5 7 7 8 2-1 7-4 7-8a7 7 0 0 0-7-7z" />
    <path d="M10 10l1.5 1.5L14.5 8" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Driving Licence Icon (document with check)
const PermitIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M9 15l2 2 4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Blue check shield for the detail card
const BlueCheckShield = () => (
  <div className="safety-shield-badge" aria-hidden>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="#0066ff" stroke="#0066ff" />
      <path d="M9 12l2 2 4-4" stroke="#ffffff" strokeWidth="2.5" />
    </svg>
  </div>
);

// Format date nicely to "DD MMM YYYY"
const formatCustomOrFallbackDate = (rawDate, fallback) => {
  if (!rawDate) return fallback;
  try {
    const d = new Date(rawDate);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  } catch {
    /* fallback */
  }
  return String(rawDate || fallback);
};

export default function VehicleSafetyReport({ driver, ride, t = (k) => k }) {
  const [activeTab, setActiveTab] = useState('rc');
  const safety = ride?.vehicleSafety || driver?.vehicleSafety || null;
  const safetyValidCount = ['rc', 'insurance', 'drivingLicence']
    .filter((key) => safety?.[key]?.status === 'valid').length;
  const unavailable = t('driver.safetyUnavailable') || 'Not published';
  const statusLabel = (status) => ({
    valid: t('driver.safetyValid') || 'Valid',
    pending: t('driver.safetyPending') || 'Pending review',
    expired: t('driver.safetyExpired') || 'Expired',
    unavailable,
  }[status] || unavailable);
  const safetyItem = (key) => safety?.[key] || {};

  const tabConfig = {
    rc: {
      label: t('driver.safetyRc') || 'RC Status',
      validUpto: formatCustomOrFallbackDate(safetyItem('rc').validUpto, unavailable),
      status: statusLabel(safetyItem('rc').status),
      icon: <RcIcon />,
    },
    insurance: {
      label: t('driver.safetyInsurance') || 'Vehicle Insurance',
      validUpto: formatCustomOrFallbackDate(safetyItem('insurance').validUpto, unavailable),
      status: statusLabel(safetyItem('insurance').status),
      icon: <InsuranceIcon />,
    },
    drivingLicence: {
      label: t('driver.safetyDrivingLicence') || 'Driving Licence (DL)',
      validUpto: formatCustomOrFallbackDate(safetyItem('drivingLicence').validUpto, unavailable),
      status: statusLabel(safetyItem('drivingLicence').status),
      icon: <PermitIcon />,
    },
  };

  const currentTab = tabConfig[activeTab] || tabConfig.rc;

  const handleTabClick = (key) => {
    hapticSelection();
    setActiveTab(key);
  };

  return (
    <div className="safety-report-card">
      {/* Header */}
      <div className="safety-report-header">
        <h4 className="safety-report-title">{t('driver.safetyTitle') || 'Auto Safety Report'}</h4>
        <p className="safety-report-subtitle">
          {t('driver.safetySubtitle') || 'Your Safety Matters to Us ❤️'}
        </p>
      </div>

      {/* 3 Interactive Tabs */}
      <div className="safety-tabs-strip" role="tablist" aria-label={t('driver.safetyTitle') || 'Auto Safety Report'}>
        {Object.entries(tabConfig).map(([key, tab]) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`safety-tab-btn ${isActive ? 'active' : ''}`}
              onClick={() => handleTabClick(key)}
            >
              <div className="safety-tab-icon-wrap">
                {tab.icon}
              </div>
              <span className="safety-tab-label">{tab.label}</span>
              {isActive && <span className="safety-tab-pointer" aria-hidden />}
            </button>
          );
        })}
      </div>

      {/* Selected Tab Detail Card (Matches Screenshot Dual-Item Design) */}
      <div className="safety-detail-box">
        <div className="safety-detail-col">
          <BlueCheckShield />
          <div className="safety-detail-text">
            <span className="safety-detail-label">{t('driver.safetyValidUpto') || 'Valid Upto'}</span>
            <span className="safety-detail-value">{currentTab.validUpto}</span>
          </div>
        </div>

        <div className="safety-detail-divider" aria-hidden />

        <div className="safety-detail-col">
          <BlueCheckShield />
          <div className="safety-detail-text">
            <span className="safety-detail-label">{t('driver.safetyStatus') || 'Status'}</span>
            <span className="safety-detail-value status-valid">{currentTab.status}</span>
          </div>
        </div>
      </div>

      {/* Official Reassurance Footer */}
      <div className="safety-report-footer">
        <span className="safety-footer-icon" aria-hidden>🛺</span>
        <span className="safety-footer-text">
          {safety?.published && safetyValidCount === 3
            ? (t('driver.safetyFooter') || 'This auto meets all Min of Road & Transport Safety checks')
            : safety?.published
              ? (t('driver.safetyFooterPartial') || 'Most safety records are verified; one or more require review')
            : (t('driver.safetyFooterUnavailable') || 'Safety details have not been published for this auto')}
        </span>
      </div>
    </div>
  );
}
